import { useEffect, useId, useRef } from 'react'
import { DisabledReason } from '../../platform/access/DisabledReason'
import { PremiumGate } from '../../platform/access/PremiumGate'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Button } from '../../platform/primitives/Button'
import { ButtonPreview } from '../../platform/primitives/ButtonPreview'
import { useBlobDownload } from '../../platform/useBlobDownload'
import type { ReportQueryRequest } from '../cashflow/useCashflow'
import { fetchReportPdf, MAX_PDF_PERIODS, PdfExportError } from './reportPdf'
import { emitExported, type ExportOutcome } from './reportsAnalytics'
import { activePreset, PRESET_TITLES, type ReportSpec } from './reportSpec'

const VARIANT = 'outline'
const SIZE = 'page'

export const PDF_FAILED = 'Export failed. Try again.'

/** What to do about a view with more than 13 columns. A quarter view has nowhere larger to go. */
export function pdfTooManyReason(columns: ReportSpec['columns']): string {
  return columns === 'quarter'
    ? `PDF fits up to ${MAX_PDF_PERIODS} columns, shorten the range`
    : `PDF fits up to ${MAX_PDF_PERIODS} columns, switch to Quarter`
}

type Props = {
  /** The table's request: the PDF is the same report, every row. */
  request: ReportQueryRequest
  spec: ReportSpec
  /** The table response's bucket count, or null while it is not known. A count of columns, never money. */
  periods: number | null
}

/**
 * Export PDF (C1, C5): the server renders `GET /reports/query/pdf` from the table's own request. Gated on
 * `export_data` and nothing else; unentitled, `PremiumGate` shows the locked preview and no request is ever made.
 *
 * A preset's name becomes the PDF's title; a custom view sends none, so the server's generated one applies.
 * More than 13 buckets is refused before the request, with the reason beside the button; the server's 422 for it
 * reads the same. Like the CSV export the button is `aria-disabled`, never `disabled`, so focus stays on it.
 */
export function ExportButton({ request, spec, periods }: Props) {
  const reasonId = useId()
  const preview = (
    <ButtonPreview variant={VARIANT} size={SIZE}>
      Export PDF
    </ButtonPreview>
  )
  const tooMany = periods !== null && periods > MAX_PDF_PERIODS
  return (
    <PremiumGate entitlement="export_data" label="PDF export" preview={preview}>
      {tooMany ? (
        <DisabledReason reason={pdfTooManyReason(spec.columns)} reasonId={reasonId}>
          <Button type="button" size={SIZE} variant={VARIANT} aria-disabled="true" aria-describedby={reasonId}>
            Export PDF
          </Button>
        </DisabledReason>
      ) : (
        <Download request={request} spec={spec} />
      )}
    </PremiumGate>
  )
}

function Download({ request, spec }: Pick<Props, 'request' | 'spec'>) {
  const toast = useToast()
  const { busy, run: download } = useBlobDownload()
  // The toast's Try again outlives the render that made it. It reads the view from here, so it exports the view
  // on screen when it is pressed rather than the one that failed.
  const latest = useRef({ request, spec })
  useEffect(() => {
    latest.current = { request, spec }
  }, [request, spec])

  const run = async (): Promise<void> => {
    const { request: req, spec: sp } = latest.current
    const preset = activePreset(sp)
    const result = await download(() => fetchReportPdf(req, preset === null ? null : PRESET_TITLES[preset]))
    if (result.status === 'ignored') return
    let outcome: ExportOutcome = 'success'
    if (result.status === 'failed') {
      if (result.error instanceof PdfExportError && result.error.kind === 'too_many_periods') {
        outcome = 'too_many_periods'
        toast.show({ kind: 'error', message: pdfTooManyReason(sp.columns) })
      } else {
        outcome = 'failed'
        toast.show({ kind: 'error', message: PDF_FAILED, action: { label: 'Try again', onAct: () => void run() } })
      }
    }
    emitExported({ columns: sp.columns, rows: sp.rows, outcome })
  }

  return (
    <>
      <Button type="button" size={SIZE} variant={VARIANT} aria-busy={busy} aria-disabled={busy} onClick={() => void run()}>
        {busy ? 'Exporting…' : 'Export PDF'}
      </Button>
      {/* Present from the start so the change is announced; the focused button's own name change is not reliably spoken. */}
      <span className="sr-only" role="status">
        {busy ? 'Exporting…' : ''}
      </span>
    </>
  )
}
